import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { StorePlatform } from '@/types/store';

import { PLATFORM_LABELS, PLATFORM_LOCALES, SELECT_CLASS } from './store-constants';

interface NewListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: StorePlatform;
  /** Locales that already have a listing for this platform — excluded from the picker. */
  existingLocales: string[];
  /** Called with the chosen locale; the parent then opens the editor drawer. */
  onCreate: (locale: string) => void;
}

/** Picks a locale (from the curated per-platform set) for a brand-new listing. */
export function NewListingDialog({
  open,
  onOpenChange,
  platform,
  existingLocales,
  onCreate,
}: NewListingDialogProps): JSX.Element {
  const available = useMemo(
    () => PLATFORM_LOCALES[platform].filter((l) => !existingLocales.includes(l)),
    [platform, existingLocales],
  );
  const [locale, setLocale] = useState('');

  useEffect(() => {
    if (open) setLocale(available[0] ?? '');
  }, [open, available]);

  const noneLeft = available.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New {PLATFORM_LABELS[platform]} listing</DialogTitle>
          <DialogDescription>
            Pick a locale to draft copy for. You can edit and publish it on the next screen.
          </DialogDescription>
        </DialogHeader>

        {noneLeft ? (
          <p className="text-sm text-muted-foreground">
            Every curated locale already has a listing for {PLATFORM_LABELS[platform]}. Edit an
            existing one from the table.
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="new-listing-locale">Locale</Label>
            <select
              id="new-listing-locale"
              className={SELECT_CLASS}
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {available.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={noneLeft || !locale}
            onClick={() => {
              onCreate(locale);
              onOpenChange(false);
            }}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
